import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { yupResolver } from '@hookform/resolvers/yup';
import { Card, Grid, Box, Button } from '@mui/material';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { schema } from './validationSchema';
import { useEffect, useState } from 'react';
import { generateSelectMenuItems } from 'src/utils';
import { GeneralSelectMenu } from 'src/components/general';
import useFormsModalStore from 'src/store/forms';
import { SkeletonForm } from 'src/components/skeleton';
import {
  networkCreateSocialManagerAccount,
  networkUpdateSocialManagerAccount,
  useFetchSocialManagerAccountTypeList,
} from 'src/network/social-maneger/account';
import { getDefaultValues } from './defualtValues';
import { useQueryClient } from '@tanstack/react-query';
import { SOCIAL_MANAGER_ACCOUNT_LIST } from 'src/constants/queryKeys';

const SocialManagerAccountForm = () => {
  const { modal, closeModal } = useFormsModalStore();
  const queryClient = useQueryClient();

  const { enqueueSnackbar } = useSnackbar();

  const [accountTypes, setAccountTypes] = useState([]);
  const [selectedAccountType, setSelectedAccountType] = useState('');

  const [accountTypeFilters] = useState({
    ID: -1,
  });

  const accountDetails = modal?.data;

  const defaultValues = getDefaultValues(accountDetails);
  const isEditMode = !!accountDetails;

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  // ---------------------------------------------------------------

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      SocialManagerAccountSocialManagerAccountTypeID: +selectedAccountType,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateSocialManagerAccount(accountDetails?.SocialManagerAccountID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('گردونه موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
            await queryClient.invalidateQueries({ queryKey: [SOCIAL_MANAGER_ACCOUNT_LIST] });
          }
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateSocialManagerAccount(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('گردونه موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            await queryClient.invalidateQueries({ queryKey: [SOCIAL_MANAGER_ACCOUNT_LIST] });
          }
          reset();
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const { isLoading: accountTypeListLoading, data: accountTypeListData } = useFetchSocialManagerAccountTypeList(accountTypeFilters, 0, 50);

  useEffect(() => {
    if (accountTypeListData) {
      const accountTypes = generateSelectMenuItems(
        accountTypeListData?.data?.items,
        'SocialManagerAccountTypeName',
        'SocialManagerAccountTypeID'
      );
      setAccountTypes(accountTypes);
      setSelectedAccountType(accountDetails?.SocialManagerAccountTypeID || accountTypes[0]?.id);
    }
  }, [accountTypeListData]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            {accountTypeListLoading ? (
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', lg: 'repeat(2, 1fr)' },
                  width: '100%',
                }}
              >
                <SkeletonForm number={3} />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                  }}
                >
                  <RHFTextField name="SocialManagerAccountToken" type="text" label="توکن حساب کاربری" />
                  <RHFTextField name="SocialManagerAccountKey" type="text" label="کلید حساب کاربری" />

                  <GeneralSelectMenu
                    label="نوع حساب"
                    name="SocialManagerAccountSocialManagerAccountTypeID"
                    options={accountTypes}
                    value={selectedAccountType}
                    onChangeValue={setSelectedAccountType}
                  />
                </Box>

                <div className="flex items-center gap-4 mt-6">
                  <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                    ذخیره
                  </LoadingButton>
                  <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                    {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
                  </LoadingButton>
                  <Button onClick={closeModal} variant="contained" color="error">
                    انصراف
                  </Button>
                </div>
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default SocialManagerAccountForm;
